import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Eye, Building2, Radio, RefreshCw, AlertCircle, Lock } from 'lucide-react';
import auctionViewerService, { SharedAuctionSummary } from '../../../services/auctionViewerService';
import { formatAuctionDate, formatAuctionTime } from './sharedAuctionFormat';
import './SharedAuctions.css';

const STATUS_LABEL: Record<SharedAuctionSummary['status'], string> = {
  live: 'Live',
  upcoming: 'Upcoming',
  completed: 'Completed',
  cancelled: 'Cancelled',
  draft: 'Draft',
};

const SharedAuctions: React.FC = () => {
  const [auctions, setAuctions] = useState<SharedAuctionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      setAuctions(await auctionViewerService.getShared());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shared auctions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Keeps "Live" badges honest without the viewer having to refresh.
    const timer = window.setInterval(() => load(true), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const liveCount = auctions.filter((a) => a.status === 'live').length;

  return (
    <div className="sv-page">
      <div className="sv-page-header">
        <div>
          <h1 className="sv-page-title">
            <Eye className="w-7 h-7" />
            Shared Auctions
          </h1>
          <p className="sv-page-subtitle">
            Auctions a channel partner has shared with your company. You can watch them live &mdash; view-only.
          </p>
        </div>
        {liveCount > 0 && (
          <span className="sv-live-pill">
            <span className="sv-live-dot" />
            {liveCount} live now
          </span>
        )}
      </div>

      {loading ? (
        <div className="sv-grid">
          {[0, 1, 2].map((i) => (
            <div key={i} className="sv-card sv-skeleton" />
          ))}
        </div>
      ) : error ? (
        <div className="sv-state sv-state-error">
          <AlertCircle className="w-6 h-6" />
          <p>{error}</p>
          <button type="button" onClick={() => load()} className="sv-btn sv-btn-secondary">
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      ) : auctions.length === 0 ? (
        <div className="sv-state">
          <Eye className="w-8 h-8" />
          <h3>No auctions shared with you yet</h3>
          <p>When a channel partner adds you to an auction, it will show up here.</p>
        </div>
      ) : (
        <div className="sv-grid">
          {auctions.map((a) => (
            <div key={a.id} className={`sv-card sv-card-${a.status}`}>
              <div className="sv-card-top">
                <span className="sv-card-no">{a.auction_no}</span>
                <span className={`sv-status sv-status-${a.status}`}>
                  {a.status === 'live' && <span className="sv-live-dot" />}
                  {STATUS_LABEL[a.status]}
                </span>
              </div>

              <h3 className="sv-card-title">{a.title}</h3>

              <div className="sv-card-meta">
                <span>
                  <Calendar className="w-4 h-4" />
                  {formatAuctionDate(a.auction_date)}
                </span>
                <span>
                  <Clock className="w-4 h-4" />
                  {formatAuctionTime(a.start_time)}
                  {a.end_time ? ` – ${formatAuctionTime(a.end_time)}` : ''}
                </span>
                {a.partner_company && (
                  <span>
                    <Building2 className="w-4 h-4" />
                    Managed by {a.partner_company}
                  </span>
                )}
              </div>

              {a.status === 'completed' || a.status === 'cancelled' ? (
                // View access closes when the auction ends.
                <button
                  type="button"
                  className="sv-btn sv-btn-primary"
                  disabled
                  title="This auction has ended. View access is no longer available."
                >
                  <Lock className="w-4 h-4" />
                  Auction Ended
                </button>
              ) : (
                <Link to={`/dashboard/shared/${a.id}`} className="sv-btn sv-btn-primary">
                  {a.status === 'live' ? <Radio className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {a.status === 'live' ? 'Watch Live' : 'View Auction'}
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="sv-footnote">
        <Lock className="w-3.5 h-3.5" />
        View-only access. Bidding, auction controls and reports are available only to the managing team.
      </p>
    </div>
  );
};

export default SharedAuctions;
